/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Storage types
 *
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Ilya Baranchikov
 * @version 1.25.1
 * @since 1.0.1
 */

import { IAction, IActionWithPayload } from '../app/createAction';
import {
    LOAD_STORAGE_DATA_FINISH,
    LOAD_STORAGE_DATA_START,
    REMOVE_COOKIE_DATA,
    REMOVE_STORAGE_DATA_START,
    REMOVE_STORAGE_DATA_SUCCESS,
    SET_COOKIE_DATA,
    SET_LOCAL_STORAGE_DATA,
    SET_SESSION_STORAGE_DATA,
    SET_STORAGE_DATA_SUCCESS,
} from './storageConstants';
import { ILocalStorage, ISessionStorageTimers, IStorageReducerState } from './storageReducer';

export enum ECookieKey {
    BANNER_ALTERNATIVE_CLOSED = 'BANNER_ALTERNATIVE_CLOSED',
    BANNER_ALTERNATIVE_SHOWED = 'BANNER_ALTERNATIVE_SHOWED',
    BILL_PAGE = 'BILL_PAGE',
    CACHE_IN_PROGRESS = 'CACHE_IN_PROGRESS',
    DENSITY_LIST_VIEW = 'DENSITY_LIST_VIEW',
    EXPENSE_ANALYZER_PAGE = 'EXPENSE_ANALYZER_PAGE',
    JSESSIONID = 'JSESSIONID',
    VOTING_HIDE = 'VOTING_HIDE',
    XSRF_TOKEN = 'XSRF_TOKEN',
}

export enum ELocalStorageKeys {
    DASHBOARD_SELECTED_ACCOUNT = 'DASHBOARD_SELECTED_ACCOUNT',
    CURRENT_USER_ID = 'CURRENT_USER_ID',
    LABELS_IMPORT = 'labels-import',
    PRODUCTS = 'PRODUCTS',
    SETTINGS_PROFILE_PARAMS = 'SETTINGS_PROFILE_PARAMS',
    /**
     * при переходе с виджета "Полезные ссылки"
     * нужно применять к кнопкам в футере таблицы
     * дополнительные правила блокировки
     */
    TABLE_BTN_ACTIVE = 'TABLE_BTN_ACTIVE',
    /* Скрыть плашку "Абоненты в роуминге" над таблицей абонентов */
    IS_SUBSCRIBERS_IN_ROAMING_PLATE_HIDDEN = 'IS_SUBSCRIBERS_IN_ROAMING_PLATE_HIDDEN',
    PRESELECTED_PRODUCT_CATEGORY = 'PRESELECTED_PRODUCT_CATEGORY'
}

export interface ICookies {
    [ECookieKey.BANNER_ALTERNATIVE_CLOSED]: string | undefined;
    [ECookieKey.BANNER_ALTERNATIVE_SHOWED]: string | undefined;
    [ECookieKey.BILL_PAGE]: string | undefined;
    [ECookieKey.CACHE_IN_PROGRESS]: string | undefined;
    [ECookieKey.EXPENSE_ANALYZER_PAGE]: string | undefined;
    [ECookieKey.DENSITY_LIST_VIEW]: boolean;
    [ECookieKey.JSESSIONID]: string | undefined;
    [ECookieKey.VOTING_HIDE]: string | undefined;
    [ECookieKey.XSRF_TOKEN]: string | undefined;
}

export type TLoadCookiesAction = IAction<typeof LOAD_STORAGE_DATA_START>;

export interface ISetCookiePayload {
    key: ECookieKey;
    options?: object;
    value: any;
}

export interface IRemoveCookiePayload {
    key: ECookieKey;
}

export type TFinishLoadStorageDataAction = IActionWithPayload<typeof LOAD_STORAGE_DATA_FINISH, IStorageReducerState>;
export type TRemoveStorageDataStartAction = IAction<typeof REMOVE_STORAGE_DATA_START>;
export type TRemoveStorageDataSuccessAction = IAction<typeof REMOVE_STORAGE_DATA_SUCCESS>;
export type TSetLocalStorageDataAction = IActionWithPayload<typeof SET_LOCAL_STORAGE_DATA, ILocalStorage>;

export interface ISetSessionStorageDataPayload {
    key: string;
    value: ISessionStorageTimers[];
}

export type TSetSessionStorageDataAction = IActionWithPayload<
    typeof SET_SESSION_STORAGE_DATA,
    ISetSessionStorageDataPayload
>;
export type TSetStorageDataSuccessAction = IAction<typeof SET_STORAGE_DATA_SUCCESS>;
export type TSetCookieAction = IActionWithPayload<typeof SET_COOKIE_DATA, ISetCookiePayload>;
export type TRemoveCookieAction = IActionWithPayload<typeof REMOVE_COOKIE_DATA, IRemoveCookiePayload>;

export type TStorageActionTypes =
    | TLoadCookiesAction
    | TFinishLoadStorageDataAction
    | TRemoveStorageDataStartAction
    | TRemoveStorageDataSuccessAction
    | TRemoveCookieAction
    | TSetCookieAction
    | TSetLocalStorageDataAction
    | TSetSessionStorageDataAction
    | TSetStorageDataSuccessAction;
